import { useCallback, useEffect, useMemo, useState } from 'react'
import ApiService from '@services/api'
import { useNotification } from '@context/NotificationContext'
import OverflowAwareContainer from '@components/shared/OverflowAwareContainer'

const EMPTY_CONFIG = { nomeCliente: '', responsaveis: [] }
const DATAJUD_STATUS_LABELS = {
  FOUND: 'Enriquecido',
  FOUND_PARTIAL: 'Enriquecido parcialmente',
  NOT_FOUND: 'Não localizado',
  INVALID_PROCESS_NUMBER: 'Processo inválido',
  QUERY_LIMIT_REACHED: 'Limite da execução',
  TIME_BUDGET_REACHED: 'Tempo da execução',
  DATAJUD_RATE_LIMITED: 'Limite do CNJ',
  DATAJUD_AUTHENTICATION_FAILED: 'Chave recusada',
  DATAJUD_NOT_CONFIGURED: 'Não configurado',
  INVALID_CONFIGURATION: 'Configuração inválida',
  DATAJUD_INVALID_RESPONSE: 'Resposta inválida',
  DATAJUD_PARTIAL_RESPONSE: 'Resposta parcial',
  DATAJUD_RESPONSE_TOO_LARGE: 'Resposta excedeu limite',
  DATAJUD_QUERY_REJECTED: 'Consulta recusada',
  DATAJUD_TIMEOUT: 'Timeout',
  DATAJUD_UNAVAILABLE: 'Indisponível',
  DATAJUD_UPSTREAM_ERROR: 'Falha no CNJ',
  UNSUPPORTED_TRIBUNAL: 'Tribunal não suportado',
  DATAJUD_ERROR: 'Erro',
}

function ExecutionsTable({ executions, selectedClientName }) {
  return (
    <section className="publications-card">
      <header className="publications-card-header">
        <h3>Execuções do robô ({selectedClientName || 'todos'})</h3>
      </header>
      <OverflowAwareContainer className="publications-table-wrapper">
        <table className="users-table publications-table">
          <thead>
            <tr>
              <th>Início</th>
              <th>Cliente</th>
              <th>Status</th>
              <th>Encontrados</th>
              <th>Importados</th>
              <th>Ignorados</th>
              <th>Erros</th>
              <th>Detalhe</th>
            </tr>
          </thead>
          <tbody>
            {executions.map((row) => (
              <tr key={row.id}>
                <td>{new Date(row.iniciadoEm).toLocaleString()}</td>
                <td>{row.clienteNome}</td>
                <td>{row.status}</td>
                <td>{row.encontrados}</td>
                <td>{row.importados}</td>
                <td>{row.ignorados}</td>
                <td>{row.erros}</td>
                <td>{row.erroMensagem || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </OverflowAwareContainer>
    </section>
  )
}

function PublicationsTable({ publications }) {
  return (
    <section className="publications-card">
      <header className="publications-card-header">
        <h3>Publicações coletadas</h3>
      </header>
      <OverflowAwareContainer className="publications-table-wrapper">
        <table className="users-table publications-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Cliente</th>
              <th>Tribunal</th>
              <th>Processo</th>
              <th>DataJud</th>
              <th>OAB</th>
              <th>Score</th>
              <th>Review</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {publications.map((row) => (
              <tr key={row.id}>
                <td>{row.dataDisponibilizacao || '-'}</td>
                <td>{row.clienteNome}</td>
                <td>{row.tribunal || '-'}</td>
                <td>{row.numeroProcesso || '-'}</td>
                <td>{DATAJUD_STATUS_LABELS[row.dataJudStatus] || row.dataJudStatus || '-'}</td>
                <td>{row.oabNumero ? `${row.oabNumero}/${row.oabUf || ''}` : '-'}</td>
                <td>{row.matchScore}</td>
                <td>{row.reviewRequired ? 'Sim' : 'Não'}</td>
                <td>{row.reviewReason || row.matchRazao}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </OverflowAwareContainer>
    </section>
  )
}

export default function PublicationsMonitoringPage() {
  const notify = useNotification()
  const [configs, setConfigs] = useState([])
  const [executions, setExecutions] = useState([])
  const [publications, setPublications] = useState([])
  const [selectedConfigId, setSelectedConfigId] = useState('')
  const [reviewOnly, setReviewOnly] = useState(false)
  const [isRunning, setIsRunning] = useState(false)

  const selectedConfig = useMemo(
    () => configs.find((x) => String(x.id) === String(selectedConfigId)) || EMPTY_CONFIG,
    [configs, selectedConfigId],
  )

  const loadAll = useCallback(async () => {
    const [cfg, ex, pb] = await Promise.all([
      ApiService.getMonitoramentoConfiguracoes(),
      ApiService.getMonitoramentoExecucoes(),
      ApiService.getMonitoramentoPublicacoes({ reviewRequired: reviewOnly || undefined }),
    ])

    if (cfg.success) setConfigs(cfg.items || cfg.data?.data || [])
    if (ex.success) setExecutions(ex.items || ex.data?.data || [])
    if (pb.success) setPublications(pb.items || pb.data?.data || [])
  }, [reviewOnly])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  const handleManualRun = async () => {
    if (!selectedConfigId || isRunning) return

    setIsRunning(true)
    try {
      const result = await ApiService.executarColetaManual({
        clienteMonitoramentoId: Number(selectedConfigId),
      })

      if (!result.success) {
        notify.showError(result.error || 'Falha ao executar coleta manual.')
        return
      }

      const execution = result.data || {}
      if ((execution.status || execution.Status) !== 'concluido') {
        notify.showError(execution.erroMensagem || execution.ErroMensagem || 'A coleta foi registrada, mas terminou com erro.')
      } else {
        notify.showSuccess('Coleta manual concluída com sucesso.')
      }
      await loadAll()
    } finally {
      setIsRunning(false)
    }
  }

  return (
    <div className="main-content publications-page">
      <div className="page-header">
        <h1 className="page-title">
          <i className="fas fa-newspaper" /> Monitoramento de Publicações
        </h1>

        <div className="page-actions publications-actions">
          <select value={selectedConfigId} onChange={(e) => setSelectedConfigId(e.target.value)}>
            <option value="">Selecione o cliente</option>
            {configs.map((cfg) => (
              <option key={cfg.id} value={cfg.id}>
                {cfg.nomeCliente}
              </option>
            ))}
          </select>

          <label className="publications-review-toggle">
            <input type="checkbox" checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)} />
            <span>Somente review required</span>
          </label>

          <button className="btn btn-primary" onClick={handleManualRun} disabled={!selectedConfigId || isRunning}>
            <i className={isRunning ? 'fas fa-spinner fa-spin' : 'fas fa-play'} /> {isRunning ? 'Executando...' : 'Executar coleta manual'}
          </button>
        </div>
      </div>

      <p className="publications-source-note">
        O DJEN permanece como fonte das publicações; quando habilitado, o CNJ/DataJud complementa os processos com metadados públicos de capa e movimentações.
      </p>

      <ExecutionsTable executions={executions} selectedClientName={selectedConfig.nomeCliente} />
      <PublicationsTable publications={publications} />
    </div>
  )
}
